/**
 * Shared vendor matching utilities.
 *
 * Used by SyncSelectedBillsfromXero, ReviewDirectBills, and
 * ProcessSelectedInvoices to match incoming vendor names against the
 * JobTread vendor list.
 */

const BUSINESS_SUFFIXES = new Set([
  'ltd', 'limited', 'llc', 'inc', 'incorporated', 'corp', 'corporation',
  'co', 'company', 'pty', 'group', 'holdings', 'enterprises', 'services',
  'solutions', 'international', 'intl', 'nz', 'au', 'uk', 'us',
  'auckland', 'wellington', 'christchurch', 'hamilton',
  'the', 'and', '&',
]);

/**
 * Lowercase, trim, normalise unicode whitespace, collapse runs of spaces.
 */
export function normalizeForComparison(str) {
  if (!str) return '';
  return str
    .toLowerCase()
    .trim()
    .replace(/[\u00A0\u2000-\u200B\u202F\u205F\u3000]/g, ' ')
    .replace(/\s+/g, ' ');
}

/**
 * Strip every whitespace character and lowercase.
 */
export function stripAllWhitespace(str) {
  if (!str) return '';
  return str.toLowerCase().replace(/\s+/g, '');
}

/**
 * Tokenise a vendor name into significant words, dropping business suffixes
 * and punctuation.
 */
export function significantTokens(str) {
  if (!str) return [];
  return str
    .toLowerCase()
    .replace(/[.,\-()'"]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 0 && !BUSINESS_SUFFIXES.has(w));
}

/**
 * Classic Levenshtein edit-distance.
 */
export function levenshtein(a, b) {
  const la = a.length;
  const lb = b.length;
  if (la === 0) return lb;
  if (lb === 0) return la;

  let prev = Array.from({ length: lb + 1 }, (_, i) => i);
  let curr = new Array(lb + 1);

  for (let i = 1; i <= la; i++) {
    curr[0] = i;
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        prev[j] + 1,       // deletion
        curr[j - 1] + 1,   // insertion
        prev[j - 1] + cost  // substitution
      );
    }
    [prev, curr] = [curr, prev];
  }
  return prev[lb];
}

/**
 * Normalised similarity between 0 and 1 using Levenshtein distance.
 * Operates on whitespace-stripped, lowercased strings.
 */
export function levenshteinSimilarity(str1, str2) {
  const a = stripAllWhitespace(str1);
  const b = stripAllWhitespace(str2);
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1;
  return 1 - levenshtein(a, b) / maxLen;
}

/**
 * Ratio of overlapping significant tokens between two vendor names.
 * Returns a value between 0 and 1.
 */
export function tokenOverlapScore(name1, name2) {
  const t1 = significantTokens(name1);
  const t2 = significantTokens(name2);
  if (t1.length === 0 || t2.length === 0) return 0;

  const set2 = new Set(t2);
  const matches = t1.filter(w => set2.has(w)).length;
  return matches / Math.min(t1.length, t2.length);
}

/**
 * Check whether the first significant word of both names matches.
 * Handles the "Continuous spouting auckland limited" ↔ "Continuous Group" case.
 */
export function firstWordMatches(name1, name2) {
  const t1 = significantTokens(name1);
  const t2 = significantTokens(name2);
  if (t1.length === 0 || t2.length === 0) return false;
  return t1[0] === t2[0];
}

/**
 * Comprehensive similarity check used for fuzzy candidate collection.
 * Returns true when the names are close enough to present as a candidate.
 * Threshold is intentionally loose because candidates require user confirmation.
 */
export function isSimilarEnough(str1, str2, threshold = 0.6) {
  const n1 = normalizeForComparison(str1);
  const n2 = normalizeForComparison(str2);
  if (n1 === n2) return true;

  const s1 = stripAllWhitespace(str1);
  const s2 = stripAllWhitespace(str2);
  if (s1.includes(s2) || s2.includes(s1)) return true;

  if (levenshteinSimilarity(str1, str2) >= threshold) return true;

  if (tokenOverlapScore(str1, str2) >= 0.5) return true;

  if (firstWordMatches(str1, str2)) return true;

  return false;
}

/**
 * Find candidate matches for a vendor name from the JT vendor list.
 *
 * Returns `{ exactMatch, candidates }` where:
 *  - `exactMatch` is a single vendor if an exact (normalised) match or alias
 *    was found, otherwise null.
 *  - `candidates` is an array of fuzzy-match vendors for user confirmation
 *    (empty when an exact match exists).
 *
 * @param {string} vendorName        The source vendor name to match.
 * @param {Array}  jtVendors         Full list of JT vendors ({ id, name }).
 * @param {Object} vendorAliases     Map of normalised-name → JT vendor ID.
 * @returns {{ exactMatch: object|null, candidates: object[] }}
 */
export function findVendorCandidates(vendorName, jtVendors, vendorAliases = {}) {
  const normalizedInput = normalizeForComparison(vendorName);
  const strippedInput = stripAllWhitespace(vendorName);

  // Strategy 1: Exact normalised match
  const exactMatch = jtVendors.find(
    v => normalizeForComparison(v.name) === normalizedInput
  );
  if (exactMatch) return { exactMatch, candidates: [] };

  // Strategy 2: Known alias lookup
  const aliasVendorId = vendorAliases[normalizedInput];
  if (aliasVendorId) {
    const aliasMatch = jtVendors.find(v => v.id === aliasVendorId);
    if (aliasMatch) return { exactMatch: aliasMatch, candidates: [] };
    console.log(`Alias for "${vendorName}" points to missing vendor ID ${aliasVendorId}, skipping`);
  }

  // Strategies 3–7: collect candidates (user must confirm)
  const candidateMap = new Map();

  for (const vendor of jtVendors) {
    const normalizedVendor = normalizeForComparison(vendor.name);

    // Strategy 3: Bidirectional substring containment
    if (normalizedVendor.includes(normalizedInput) || normalizedInput.includes(normalizedVendor)) {
      candidateMap.set(vendor.id, vendor);
      continue;
    }

    // Strategy 4: Whitespace-stripped exact
    if (stripAllWhitespace(vendor.name) === strippedInput) {
      candidateMap.set(vendor.id, vendor);
      continue;
    }

    // Strategy 5–7: Fuzzy (Levenshtein + token overlap + first-word)
    if (isSimilarEnough(vendor.name, vendorName)) {
      candidateMap.set(vendor.id, vendor);
    }
  }

  return { exactMatch: null, candidates: Array.from(candidateMap.values()) };
}

/**
 * Normalise a /api/jt/createvendor response into `{ id, name, type }`.
 * Returns null when JobTread did not return a usable account.
 */
export function parseCreatedVendor(result, fallbackName = '') {
  if (!result || typeof result !== 'object') return null;
  const account = result.createdAccount?.id
    ? result.createdAccount
    : (result.id ? result : null);
  if (!account?.id) return null;
  return {
    id: account.id,
    name: account.name || fallbackName,
    type: account.type || 'vendor'
  };
}

export const JT_REQUIRED_CUSTOM_FIELD = 'JT_REQUIRED_CUSTOM_FIELD';

export function isRequiredCustomFieldResult(result) {
  return result?.code === JT_REQUIRED_CUSTOM_FIELD
    && Array.isArray(result.requiredCustomFields)
    && result.requiredCustomFields.length > 0;
}

export function mergeRequiredCustomFields(existing = [], incoming = []) {
  const byKey = new Map();
  for (const field of [...existing, ...incoming]) {
    if (!field) continue;
    const key = field.id || `name:${field.name}`;
    if (!byKey.has(key)) byKey.set(key, field);
  }
  return [...byKey.values()];
}

export function customFieldValuesFromSelections(values = {}) {
  const out = {};
  for (const [id, value] of Object.entries(values || {})) {
    if (value != null && String(value).trim() !== '') out[id] = String(value).trim();
  }
  return out;
}

export function requiredCustomFieldsFilled(fields = [], values = {}) {
  if (!fields.length) return true;
  return fields.every((field) => {
    const value = values[field.id];
    return value != null && String(value).trim() !== '';
  });
}
